/**
 * Rio sketches the bar for a recruiter's question: the area it belongs to
 * and what an excellent, adequate and weak answer shows. It happens once,
 * when a question has none yet (just added, or saved before schema 2), and
 * the sketch lands on the config as if Rio had typed it in. The recruiter
 * then edits any line in place.
 */
import { useEffect, useRef } from 'react';
import type {
  InterviewerConfig,
  SectionKey,
} from '../../services/interviewer.types';
import { useInterviewerService } from '../../interviewer/serviceContext';
import { setExpectations } from '../../interviewer/model';

interface Options {
  config: InterviewerConfig;
  onChange: (change: (current: InterviewerConfig) => InterviewerConfig) => void;
  /** The questions to sketch: those without a bar. */
  enabled: boolean;
  onSketching?: (ids: Set<string>) => void;
}

export const useQuestionBar = ({
  config,
  onChange,
  enabled,
  onSketching,
}: Options) => {
  const service = useInterviewerService();
  const inFlight = useRef(new Set<string>());
  // A reply after unmount is dropped; a question removed meanwhile is a no-op.
  const mounted = useRef(true);
  const configRef = useRef(config);
  configRef.current = config;

  // Only the questions still without a bar. The in-flight set is deliberately
  // not part of this key: telling the card "sketching" re-renders it, and a
  // key that changed with it would clean this effect up and discard the reply.
  const pending = config.customQuestions
    .filter((q) => !q.expectations)
    .map((q) => q.id)
    .join();

  useEffect(() => {
    if (!enabled || !pending) return;
    const ids = pending.split(',').filter((id) => !inFlight.current.has(id));
    if (!ids.length) return;
    ids.forEach((id) => inFlight.current.add(id));
    onSketching?.(new Set(inFlight.current));
    (async () => {
      try {
        const result = await service.validate(configRef.current);
        if (!mounted.current || !result.preview) return;
        // Rio answers by id; a sketch that came back under another id (a
        // placeholder from the live check, say) is matched on the question's words.
        const byId = new Map(
          result.preview.customQuestions.map((q) => [q.id, q] as const),
        );
        const byText = new Map(
          result.preview.customQuestions.map(
            (q) => [q.text.trim(), q] as const,
          ),
        );
        onChange((current) =>
          ids.reduce((acc, id) => {
            const own = current.customQuestions.find((q) => q.id === id);
            const sketch =
              byId.get(id) ?? (own ? byText.get(own.text.trim()) : undefined);
            if (!sketch?.expectations) return acc;
            return setExpectations(
              acc,
              id,
              sketch.expectations,
              sketch.area
                ? { key: sketch.area, label: sketch.areaLabel ?? sketch.area }
                : null,
            );
          }, current),
        );
      } catch {
        // No sketch: the recruiter can still write the bar by hand.
      } finally {
        ids.forEach((id) => inFlight.current.delete(id));
        if (mounted.current) onSketching?.(new Set(inFlight.current));
      }
    })();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, enabled, service]);

  useEffect(() => {
    mounted.current = true;
    const flying = inFlight.current;
    return () => {
      mounted.current = false;
      flying.clear();
    };
  }, []);
};

export type { SectionKey };
